<script setup lang="ts">
// Ports legacy OTooltip (171 call-sites). Positioning goes through the shared useFloating
// adapter. Shows on hover/focus, keeps pointer hover across the floating panel, never traps focus.
import { computed, onBeforeUnmount, ref, useSlots } from 'vue'

import { isOverflowing } from './isOverflowing'
import type { TooltipProps } from './types'

import { useFloating } from '@/shared/ui/floating'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'
import { sanitizeHtml } from '@/shared/ui/sanitize'

const props = withDefaults(defineProps<TooltipProps>(), {
  placement: 'top',
  onlyOnOverflow: false,
  html: false,
})

const reference = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)
const visible = ref(false)
const sanitisedText = computed(() => (props.html ? sanitizeHtml(props.text ?? '') : ''))
const slots = useSlots()
let hideTimer: ReturnType<typeof setTimeout> | undefined

// A tooltip with no text and no `#content` override has nothing to show — surfacing it anyway
// renders an empty, padded bubble on hover (a stray box with no readable content).
function hasContent(): boolean {
  return Boolean(props.text?.trim()) || Boolean(slots.content)
}

const { floatingStyles } = useFloating(reference, floating, {
  placement: props.placement,
  offset: 6,
})

function cancelHide(): void {
  if (hideTimer === undefined) return
  clearTimeout(hideTimer)
  hideTimer = undefined
}

function show(): void {
  cancelHide()
  if (!hasContent()) return
  if (props.onlyOnOverflow && (!reference.value || !isOverflowing(reference.value))) return
  visible.value = true
}

function hideSoon(): void {
  cancelHide()
  hideTimer = setTimeout(() => {
    visible.value = false
    hideTimer = undefined
  }, 80)
}

function onFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget
  if (next instanceof Node && (reference.value?.contains(next) || floating.value?.contains(next))) {
    return
  }
  hideSoon()
}

onBeforeUnmount(cancelHide)
</script>

<template>
  <span
    ref="reference"
    class="ui-tooltip-trigger"
    :class="props.block ? 'flex w-full' : 'inline-flex'"
    @mouseenter="show"
    @mouseleave="hideSoon"
    @focusin="show"
    @focusout="onFocusOut"
  >
    <slot />
  </span>

  <OverlayPortal>
    <FloatingLayer
      v-if="visible"
      v-model:element="floating"
      kind="tooltip"
      class="ui-tooltip pointer-events-auto w-max max-w-64 rounded-sm bg-fg px-xs py-1.5 font-sans text-xs leading-[1.3] text-surface shadow-pop"
      :style="floatingStyles"
      role="tooltip"
      @mouseenter="cancelHide"
      @mouseleave="hideSoon"
    >
      <slot name="content">
        <!-- eslint-disable-next-line vue/no-v-html -- only the DOMPurify output reaches v-html -->
        <span v-if="html" v-html="sanitisedText" />
        <template v-else>{{ text }}</template>
      </slot>
    </FloatingLayer>
  </OverlayPortal>
</template>
